import { Lesson, Training, TrainingWithLessons } from "@/app/entities/Training";
import { PaginationResponse } from "@/app/types";
import { LessonFormValues } from "@/view/pages/Conteudos/hooks/useClssForm";
import { TrainingFormValues } from "@/view/pages/Conteudos/hooks/useTrainingForm";
import { httpClient } from "../http/httpClient";

interface TrainingPaginate extends PaginationResponse {
  trainings: Training[];
}

const API_URL = "/training";
const LESSON_API_URL = "/lesson";

const getVideoDuration = async (videoFile: File) => {
  const loadVideo = (file: File): Promise<HTMLVideoElement> =>
    new Promise((resolve, reject) => {
      try {
        const video = document.createElement("video");
        video.preload = "metadata";

        video.onloadedmetadata = function () {
          resolve(this as HTMLVideoElement);
        };

        video.onerror = function () {
          reject(new Error("Invalid video. Please select a video file."));
        };

        video.src = window.URL.createObjectURL(file);
      } catch (e) {
        reject(e);
      }
    });

  const video = await loadVideo(videoFile);
  return video?.duration;
};

export const trainingService = {
  async getTrainings(skip: number, limit: number, search: string) {
    const { data } = await httpClient.get<TrainingPaginate>(API_URL, {
      params: { skip, limit, search },
    });
    return data;
  },

  async deleteLesson(lessonId: string) {
    await httpClient.delete(`${LESSON_API_URL}/${lessonId}`);
  },

  async getTrainingById(trainingId: string): Promise<TrainingWithLessons> {
    const { data } = await httpClient.get<TrainingWithLessons>(
      `${API_URL}/${trainingId}`,
    );
    return data;
  },

  async createTraining(training: TrainingFormValues): Promise<Training> {
    const { data } = await httpClient.post<Training>(API_URL, training);
    return data;
  },

  async updateTraining(trainingId: string, training: TrainingFormValues) {
    const { data } = await httpClient.put<Training>(
      `${API_URL}/${trainingId}`,
      training,
    );
    return data;
  },

  async deleteTraining(trainingId: string) {
    await httpClient.delete(`${API_URL}/${trainingId}`);
  },

  async getLessonsByTrainingId(trainingId: string) {
    const { data } = await httpClient.get<{ lessons: Lesson[] }>(
      `${API_URL}/${trainingId}/lesson`,
    );
    return data;
  },

  async getLessonById(lessonId: string) {
    const { data } = await httpClient.get<{ lesson: Lesson }>(
      `${LESSON_API_URL}/${lessonId}`,
    );
    return data;
  },

  async addLessonToTraining(trainingId: string, lesson: LessonFormValues) {
    let duration = 0;
    const isYoutubeVideo = lesson.isYoutubeVideo;

    if (!isYoutubeVideo && lesson.videoFile) {
      duration = await getVideoDuration(lesson.videoFile);
    }

    if (isYoutubeVideo) {
      duration = lesson.duration ? lesson.duration : 0;
    }

    const { data } = await httpClient.post<Lesson>(
      `${API_URL}/${trainingId}/lesson`,
      { ...lesson, duration, link: lesson?.videoUrl },
    );

    const lessonId = data?._id;
    const formData = new FormData();
    formData.append("thumbnail_url", lesson?.thumbnailFile || "");

    if (isYoutubeVideo && lesson?.thumbnailFile) {
      await httpClient.put(`${LESSON_API_URL}/content/${lessonId}`, formData);
    }

    if (!isYoutubeVideo && lesson.videoFile) {
      formData.append("video_url", lesson.videoFile);
      await httpClient.put(`${LESSON_API_URL}/content/${lessonId}`, formData);
    }

    const hasAttachments = lesson?.files?.length || 0;
    if (!hasAttachments) return data;

    lesson?.files?.forEach(async (file) => {
      const formData = new FormData();
      formData.append("file", file.file);
      formData.append("name", file.name);
      await httpClient.put(
        `${LESSON_API_URL}/attachment/${lessonId}`,
        formData,
      );
    });

    return data;
  },

  async updateLesson(lessonId: string, lesson: LessonFormValues) {
    const isYoutubeVideo = lesson.isYoutubeVideo;
    let duration = 0;

    if (!isYoutubeVideo && lesson.videoFile) {
      duration = await getVideoDuration(lesson.videoFile);
    }

    await httpClient.put<Lesson>(`${LESSON_API_URL}/${lessonId}`, {
      ...lesson,
      duration,
      link: lesson?.videoUrl,
    });

    const formData = new FormData();
    formData.append("thumbnail_url", lesson?.thumbnailFile || "");

    if (!isYoutubeVideo && lesson.videoFile) {
      formData.append("video_url", lesson.videoFile);
      await httpClient.put(`${LESSON_API_URL}/content/${lessonId}`, formData);
    }

    const hasAttachments = lesson?.files?.length || 0;
    if (!hasAttachments) return;

    lesson?.files?.forEach(async (file) => {
      const formData = new FormData();
      formData.append("file", file.file);
      formData.append("name", file.name);
      await httpClient.put(
        `${LESSON_API_URL}/attachment/${lessonId}`,
        formData,
      );
    });
  },

  async deleteAttachment(attachmentId: string) {
    await httpClient.delete(`${LESSON_API_URL}/attachment/${attachmentId}`);
  },
};
